import React from 'react';

interface SkeletonLoaderProps {
  /**
   * Width of the skeleton line (default: '100%')
   */
  width?: string | number;
  /**
   * Height of the skeleton line (default: '1.5rem')
   */
  height?: string | number;
  /**
   * Number of lines to render (default: 1)
   */
  lines?: number;
  /**
   * Margin between lines (default: '0.5rem')
   */
  marginBottom?: string | number;
  /**
   * Custom class name for the skeleton line
   */
  className?: string;
  /**
   * Inline styles for the container
   */
  style?: React.CSSProperties;
  /**
   * Type of skeleton: 'line' (default), 'circle', or 'rect'
   */
  variant?: 'line' | 'circle' | 'rect';
  /**
   * Border radius for 'line' and 'rect' variants (default: '0.25rem')
   */
  borderRadius?: string | number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = '100%',
  height = '1.5rem',
  lines = 1,
  marginBottom = '0.5rem',
  className = '',
  style = {},
  variant = 'line',
  borderRadius = '0.25rem'
}) => {
  // Generate skeleton lines
  const skeletonLines = Array.from({ length: lines }).map((_, index) => (
    <div
      key={index}
      className={`skeleton-loader ${variant} ${className}`}
      style={{
        width,
        height,
        marginBottom: index < lines - 1 ? marginBottom : 0,
        borderRadius: variant === 'circle' ? '50%' : borderRadius,
        ...style
      }}
    />
  ));

  // Handle circle variant (single element)
  if (variant === 'circle') {
    return (
      <div
        className={`skeleton-loader ${variant} ${className}`}
        style={{
          width,
          height,
          borderRadius: '50%',
          ...style
        }}
      />
    );
  }

  // Handle rect variant (single element)
  if (variant === 'rect') {
    return (
      <div
        className={`skeleton-loader ${variant} ${className}`}
        style={{
          width,
          height,
          borderRadius,
          ...style
        }}
      />
    );
  }

  // Default: line(s)
  return <>{skeletonLines}</>;
};

export default SkeletonLoader;
